import { DefaultButton } from "@/components/DefaultButton";
import { FloatingLeftButton } from "@/components/FloatingLeftButton";
import { PayoutSimulationDialog } from "@/components/PayoutSimulationDialog";
import { TournamentSummaryDialog } from "@/components/TournamentSummaryDialog";
import { Input } from "@/components/ui/input";
import {
  CustomSelect,
  type CustomSelectOption,
} from "@/components/ui/custom-select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  useCreateHostedTournament,
  useCreateSquad,
  useStats,
  queryClient,
} from "@/lib/hooks/useApi";
import { cn, formations } from "@/lib/utils";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import type { FormEvent } from "react";
import { toast } from "sonner";
import { Eye, EyeOff, InfoIcon } from "lucide-react";
import { usePrivy } from "@privy-io/react-auth";
import type { Player, TokenListResponse } from "@/lib/types/api";
import { SquadMatchTypeSheet } from "@/components/SquadMatchTypeSheet";
import { PlayerCard, PlayerCardCompact } from "@/components/PlayersJoinList";
import { t } from "@/lib/i18n";
import { VERIFIED_PASSWORD_STORAGE_KEY_PREFIX } from "@/components/PasswordDialog";

type FormState = {
  name: string;
  start: string;
  duration: string;
  entryFee: string;
  capacity: string;
  maxMultiEntry: string;
  payoutPlaces: string;
  matchType: "long" | "short";
  password: string;
};

const MIN_ENTRY_FEE = 0;
const MAX_ENTRY_FEE = 10;
const MAX_CAPACITY = 10;
const MIN_PASSWORD_LENGTH = 5;
const MAX_PASSWORD_LENGTH = 20;
const DURATION_OPTIONS = [
  { label: "1 Minute", value: "1m", seconds: 60 },
  { label: "5 Minutes", value: "5m", seconds: 5 * 60 },
  { label: "10 Minutes", value: "10m", seconds: 10 * 60 },
  { label: "15 Minutes", value: "15m", seconds: 15 * 60 },
  { label: "30 Minutes", value: "30m", seconds: 30 * 60 },
  { label: "1 Hour", value: "1h", seconds: 60 * 60 },
  { label: "4 Hours", value: "4h", seconds: 4 * 60 * 60 },
  { label: "12 Hours", value: "12h", seconds: 12 * 60 * 60 },
  { label: "1 Day", value: "1d", seconds: 24 * 60 * 60 },
  { label: "1 Week", value: "1w", seconds: 7 * 24 * 60 * 60 },
];
const DURATION_TO_SECONDS = DURATION_OPTIONS.reduce<Record<string, number>>(
  (acc, option) => {
    acc[option.value] = option.seconds;
    return acc;
  },
  {},
);

const padZero = (value: number) => value.toString().padStart(2, "0");
const getLocalDateString = () => {
  const now = new Date();
  return `${now.getFullYear()}-${padZero(now.getMonth() + 1)}-${padZero(
    now.getDate(),
  )}`;
};

const getDateOneWeekFromNow = () => {
  const now = new Date();
  now.setDate(now.getDate() + 7);
  return `${now.getFullYear()}-${padZero(now.getMonth() + 1)}-${padZero(
    now.getDate(),
  )}`;
};
const getLocalTimeString = () => {
  const now = new Date();
  return `${padZero(now.getHours())}:${padZero(now.getMinutes())}`;
};

const getUtcOffset = () => {
  const offset = -new Date().getTimezoneOffset(); // Negative because getTimezoneOffset returns opposite
  const hours = Math.floor(Math.abs(offset) / 60);
  const sign = offset >= 0 ? "+" : "-";
  return `UTC${sign}${hours}`;
};

export const Route = createFileRoute("/(game)/create-tournament")({
  component: CreateTournamentPage,
});

function CreateTournamentPage() {
  const navigate = useNavigate();
  const { user } = usePrivy();
  const { mutateAsync: createHostedTournament, isPending } =
    useCreateHostedTournament();
  const { mutateAsync: createSquad } = useCreateSquad();
  const { data: stats } = useStats(user?.wallet?.address ?? "");
  const gameWalletAddress = stats?.embeddedWallet ?? "";

  const defaultStartDate = getLocalDateString();
  const defaultStartTime = getLocalTimeString();
  const todayDate = getLocalDateString();
  const maxStartDate = getDateOneWeekFromNow();
  const [formState, setFormState] = useState<FormState>({
    name: "",
    start: `${defaultStartDate}T${defaultStartTime}`,
    duration: "",
    entryFee: "",
    capacity: "",
    maxMultiEntry: "",
    payoutPlaces: "",
    matchType: "long",
    password: "",
  });
  const [startDateInput, setStartDateInput] = useState(defaultStartDate);
  const [startTimeInput, setStartTimeInput] = useState(defaultStartTime);
  const [isEntryFeeMaxError, setEntryFeeMaxError] = useState(false);
  const [isCapacityMaxError, setCapacityMaxError] = useState(false);
  const [isMaxMultiEntryError, setMaxMultiEntryError] = useState(false);
  const [, setDateMaxError] = useState(false);

  const [isSimulationOpen, setSimulationOpen] = useState(false);
  const [isSimulationFromButton, setIsSimulationFromButton] = useState(false);
  const [isSummaryDialogOpen, setIsSummaryDialogOpen] = useState(false);
  const [selectedTokens, setSelectedTokens] = useState<
    (TokenListResponse | undefined)[]
  >([]);
  const [showPassword, setShowPassword] = useState(false);
  const [isSquadMatchTypeSheetOpen, setIsSquadMatchTypeSheetOpen] =
    useState(false);
  const [activeSlotIndex, setActiveSlotIndex] = useState<number | null>(null);
  // const [payoutWeights, setPayoutWeights] = useState<Weight[] | null>(null);

  const maxMultiEntryMax = useMemo(() => {
    const capacityNum = Number(formState.capacity);
    if (!Number.isFinite(capacityNum) || capacityNum <= 0) return undefined;
    const effectiveCapacity = Math.min(capacityNum, MAX_CAPACITY);
    return Math.floor(effectiveCapacity / 2);
  }, [formState.capacity]);

  const isMaxMultiEntryDisabled =
    !formState.capacity || Number(formState.capacity) <= 0;

  const durationOptions = useMemo<CustomSelectOption[]>(() => {
    const DURATION_OPTIONS = [
      {
        label: t("createTournament.durationOptions.1Minute"),
        value: "1m",
        seconds: 60,
      },
      {
        label: t("createTournament.durationOptions.5Minutes"),
        value: "5m",
        seconds: 5 * 60,
      },
      {
        label: t("createTournament.durationOptions.10Minutes"),
        value: "10m",
        seconds: 10 * 60,
      },
      {
        label: t("createTournament.durationOptions.15Minutes"),
        value: "15m",
        seconds: 15 * 60,
      },
      {
        label: t("createTournament.durationOptions.30Minutes"),
        value: "30m",
        seconds: 30 * 60,
      },
      {
        label: t("createTournament.durationOptions.1Hour"),
        value: "1h",
        seconds: 60 * 60,
      },
      {
        label: t("createTournament.durationOptions.4Hours"),
        value: "4h",
        seconds: 4 * 60 * 60,
      },
      {
        label: t("createTournament.durationOptions.12Hours"),
        value: "12h",
        seconds: 12 * 60 * 60,
      },
      {
        label: t("createTournament.durationOptions.1Day"),
        value: "1d",
        seconds: 24 * 60 * 60,
      },
      {
        label: t("createTournament.durationOptions.1Week"),
        value: "1w",
        seconds: 7 * 24 * 60 * 60,
      },
    ];
    return DURATION_OPTIONS.map(({ label, value }) => ({
      label,
      value,
    }));
  }, []);

  const payoutOptions = useMemo<CustomSelectOption[]>(() => {
    const capacityNum = Number(formState.capacity);
    if (!Number.isFinite(capacityNum) || capacityNum <= 0) {
      return [];
    }

    const options: CustomSelectOption[] = [];
    const halfCapacity = Math.floor(capacityNum / 2);

    // Only show options that are <= halfCapacity
    // Always show Top 1 if halfCapacity >= 1
    if (halfCapacity >= 1) {
      options.push({
        label: t("createTournament.payoutOptions.top1"),
        value: "Top 1",
      });
    }

    // Show Top 3 only if halfCapacity >= 3
    if (halfCapacity >= 3) {
      options.push({
        label: t("createTournament.payoutOptions.top3"),
        value: "Top 3",
      });
    }

    // Show Top 5 only if halfCapacity >= 5
    if (halfCapacity >= 5) {
      options.push({
        label: t("createTournament.payoutOptions.top5"),
        value: "Top 5",
      });
    }

    // Show Top 10 only if halfCapacity >= 10
    if (halfCapacity >= 10) {
      options.push({
        label: t("createTournament.payoutOptions.top10"),
        value: "Top 10",
      });
    }

    // Add half capacity if it's different from standard options and > 0
    if (halfCapacity > 0 && ![1, 3, 5, 10].includes(halfCapacity)) {
      options.push({
        label: t("createTournament.payoutOptions.top", {
          number: halfCapacity,
        }),
        value: `Top ${halfCapacity}`,
      });
    }

    return options;
  }, [formState.capacity]);

  const canSimulatePayout =
    Number(formState.entryFee) > 0 &&
    Number(formState.capacity) > 0 &&
    Boolean(formState.payoutPlaces);

  const handleSimulate = () => {
    if (!canSimulatePayout) {
      if (!formState.payoutPlaces) {
        toast.error("Error", {
          description: "Please select payout places first",
        });
      } else if (Number(formState.entryFee) < MIN_ENTRY_FEE) {
        toast.error("Error", {
          description: "Please enter a valid entry fee",
        });
      } else if (Number(formState.capacity) <= 0) {
        toast.error("Error", {
          description: "Please enter a valid capacity",
        });
      }
      return;
    }
    setIsSimulationFromButton(true);
    setSimulationOpen(true);
  };

  const capacityValue = Number(formState.capacity);
  const maxMultiEntryValue = Number(formState.maxMultiEntry);
  const allowableMultiEntry =
    Number.isFinite(capacityValue) && capacityValue > 0
      ? Math.floor(Math.min(capacityValue, MAX_CAPACITY) / 2)
      : undefined;
  const isCapacityValid =
    Number.isFinite(capacityValue) &&
    capacityValue > 0 &&
    capacityValue <= MAX_CAPACITY;
  const isMaxMultiEntryValid =
    Number.isFinite(maxMultiEntryValue) &&
    maxMultiEntryValue > 0 &&
    (allowableMultiEntry === undefined ||
      maxMultiEntryValue <= allowableMultiEntry);

  const filledTokensCount = selectedTokens[0] ? 1 : 0;

  const handleSelectToken = (token: TokenListResponse) => {
    if (activeSlotIndex !== null) {
      setSelectedTokens([token]);
      setActiveSlotIndex(null);
    }
  };

  const getPositionLabel = (formation: string, positionIndex: number) => {
    const formationKey = formation as keyof typeof formations;
    return formations[formationKey]?.positions[positionIndex]?.label || "";
  };

  const playerCardData = useMemo<Player | null>(() => {
    const selectedToken = selectedTokens[0];

    if (!selectedToken) {
      return null;
    }

    const filledTokens = Array<TokenListResponse>(5).fill(selectedToken);
    const dna = filledTokens.map((token) => token.pythLazerId).join(",");

    return {
      squadAddress: dna,
      squadDetails: {
        owner: gameWalletAddress || "",
        name: formState.name
          ? formState.name.toUpperCase()
          : t("createTournament.newSquad"),
        formation: "",
        squadMatchType: formState.matchType,
        index: 0,
        squadValue: 0,
        entryType: 0,
        dna,
        orderPlayer: 0,
        playerDetails: filledTokens.map((token, index) => ({
          playerName: token.tokenName,
          playerSymbol: token.tokenSymbol,
          pythLazerId: token.pythLazerId,
          playerImageUrl: token.tokenImageLogo,
          currentPrice: 0,
          startPrice: 0,
          endPrice: 0,
          playerAllocation: 0,
          positionIndex: index,
          pnlPercentage: 0,
          ephemeralPriceFeedAccount: token.solanaPythFeedId || "",
          playerValue: 0,
          multiplier: token.multiplier,
          playerPosition: token.position || "",
          playerMarketCap: token.marketCap,
          playerPower: token.currentPower,
          playerCard: token.playerCard,
          priceChange7d: token.priceChange7d,
          priceChange24h: token.priceChange24h,
          assetType: token.assetType ?? "crypto",
        })),
      },
      ownerProfile: {
        profilePicture: stats?.profilePicture || "",
        username: stats?.username ?? null,
        userPubkey: gameWalletAddress || "",
        domainType: null,
        isLegends: false,
        isVerified: stats?.isVerified ?? false,
        isDoubleTrophyActive: false,
        activeFrameUrl: stats?.activeFrameUrl || "",
      },
    };
  }, [
    selectedTokens,
    gameWalletAddress,
    formState.name,
    formState.matchType,
    stats?.profilePicture,
    stats?.username,
    stats?.isVerified,
    stats?.activeFrameUrl,
  ]);

  const passwordValid =
    !formState.password.trim() ||
    (formState.password.length >= MIN_PASSWORD_LENGTH &&
      formState.password.length <= MAX_PASSWORD_LENGTH);
  const isPasswordError =
    formState.password.trim().length > 0 && !passwordValid;

  const isFreeEntry = Number(formState.entryFee) === 0;

  const canSubmit = Boolean(
    formState.name.trim() &&
      formState.start &&
      formState.duration &&
      formState.entryFee !== "" &&
      Number(formState.entryFee) >= MIN_ENTRY_FEE &&
      Number(formState.entryFee) <= MAX_ENTRY_FEE &&
      isCapacityValid &&
      isMaxMultiEntryValid &&
      (isFreeEntry || formState.payoutPlaces) &&
      filledTokensCount === 1 &&
      passwordValid,
  );

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSubmit) return;

    // Validate form values
    const entryFeeValue = Number(formState.entryFee);
    const capacityValue = Number(formState.capacity);
    const maxMultiEntryValue = Number(formState.maxMultiEntry);
    const startDateEpoch = Math.floor(
      new Date(formState.start).getTime() / 1000,
    );
    const durationSeconds = DURATION_TO_SECONDS[formState.duration] ?? 0;

    if (
      !Number.isFinite(entryFeeValue) ||
      !Number.isFinite(capacityValue) ||
      !Number.isFinite(maxMultiEntryValue) ||
      !Number.isFinite(startDateEpoch) ||
      durationSeconds <= 0 ||
      (entryFeeValue > 0 && !formState.payoutPlaces)
    ) {
      return;
    }

    if (entryFeeValue > MAX_ENTRY_FEE) {
      toast.error("Error", {
        description: "Maximum entry fee is " + MAX_ENTRY_FEE + " USD",
      });
      return;
    }

    if (capacityValue > MAX_CAPACITY) {
      toast.error("Error", {
        description: "Maximum squad capacity is " + MAX_CAPACITY,
      });
      return;
    }

    const allowableForSubmit = Math.floor(
      Math.min(capacityValue, MAX_CAPACITY) / 2,
    );
    if (maxMultiEntryValue > allowableForSubmit) {
      toast.error("Error", {
        description: "Maximum multi entry is " + allowableForSubmit,
      });
      return;
    }

    // Validate squad selection
    if (filledTokensCount !== 1) {
      toast.error("Error", {
        description: "Please select a player for your squad",
      });
      return;
    }

    if (!passwordValid) {
      toast.error("Error", {
        description: t("createTournament.passwordLengthError", {
          min: MIN_PASSWORD_LENGTH,
          max: MAX_PASSWORD_LENGTH,
        }),
      });
      return;
    }

    // Open summary dialog from form submission
    setIsSummaryDialogOpen(true);
  };

  const handleCreateTournament = async () => {
    const entryFeeValue = Number(formState.entryFee);
    const capacityValue = Number(formState.capacity);
    const maxMultiEntryValue = Number(formState.maxMultiEntry);
    const startDateEpoch = Math.floor(
      new Date(formState.start).getTime() / 1000,
    );
    const durationSeconds = DURATION_TO_SECONDS[formState.duration] ?? 0;

    // Extract number from payout places (e.g., "Top 1" -> 1, "Top 3" -> 3, "Top 12" -> 12)
    const topWinnerMatch = formState.payoutPlaces.match(/\d+/);
    const topWinner = topWinnerMatch
      ? Number(topWinnerMatch[0])
      : entryFeeValue === 0
        ? 1
        : 0;

    if (!Number.isFinite(topWinner) || topWinner <= 0) {
      toast.error("Error", {
        description: "Invalid payout places selection",
      });
      return;
    }

    try {
      const selectedToken = selectedTokens[0];

      if (!selectedToken) {
        throw new Error("Please select a player for your squad");
      }

      const squadToUse = await createSquad({
        name: ``,
        playerIds: Array(5).fill(selectedToken.pythLazerId),
        isOneTimeUsed: true,
      });

      if (!squadToUse?.squadId) {
        throw new Error("Failed to create temporary squad");
      }

      // Get app origin
      const appOrigin =
        typeof window !== "undefined" ? window.location.origin : "";

      // Request tournament creation
      const tournamentResponse = await createHostedTournament({
        squadId: squadToUse.squadId,
        matchType: formState.matchType,
        slotCount: 1, // Default to 1 for creating tournament
        appOrigin,
        maxPlayer: capacityValue,
        entryAmountOnUsdc: entryFeeValue,
        name: formState.name.trim().toUpperCase(),
        startDate: startDateEpoch,
        duration: durationSeconds,
        maxMultiEntryCount: maxMultiEntryValue,
        topWinner,
        ...(formState.password.trim() && {
          password: formState.password.trim().toLowerCase(),
        }),
      });

      // Extract tournamentId from response
      const tournamentId = tournamentResponse.tournamentId;

      // Save password to session so owner can access without re-entering
      const passwordValue = formState.password.trim().toLowerCase();
      if (passwordValue && typeof window !== "undefined") {
        sessionStorage.setItem(`cfl:verifiedTournament:${tournamentId}`, "1");
        sessionStorage.setItem(
          `${VERIFIED_PASSWORD_STORAGE_KEY_PREFIX}${tournamentId}`,
          passwordValue,
        );
      }

      // Transaction is signed and confirmed by the server — no client signing needed

      // Close dialog
      setIsSummaryDialogOpen(false);

      toast.success("Success", {
        description: "Tournament created successfully!",
      });

      // Invalidate queries
      queryClient.invalidateQueries({
        queryKey: ["userHostedTournamentList"],
      });

      // Navigate to the newly created tournament
      // Use replace: true to remove create-tournament from history
      navigate({
        to: "/hosted-tournament/$tournamentId",
        params: { tournamentId },
        search: { fromCreate: true },
        replace: true,
      });
    } catch (error) {
      console.error("Create tournament error:", error);
      let message = "Failed to create tournament";
      if (typeof error === "object" && error !== null && "response" in error) {
        const err = error as {
          response?: { data?: { error?: string } };
        };
        message = err.response?.data?.error ?? message;
      }
      toast.error("Failed to create tournament", {
        description: message,
      });
    }
  };

  return (
    <div className="relative flex flex-col flex-1 bg-[#1D2A52] p-4 overflow-y-auto">
      <FloatingLeftButton
        onClick={() => navigate({ to: "/hosted-tournament" })}
        className="absolute left-0 top-4 z-50"
        icon={
          <svg
            className="w-4 h-4"
            viewBox="0 0 9 8"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M3 1V0H2V1H1V2H0V3H1V4H2V5H3V4H6V7H3V8H7V7H8V6H9V3H8V2H7V1H3Z"
              fill="white"
            />
          </svg>
        }
      />

      <div className="flex flex-col items-center text-center mt-0">
        <h2 className="text-3xl font-black uppercase text-shadow-black">
          {t("createTournament.create")}
        </h2>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-1 flex-col gap-2">
        <div className="space-y-2">
          <label className="text-xs uppercase tracking-wide text-white/70">
            {t("createTournament.tournamentName")}
          </label>
          <Input
            autoComplete="nope"
            value={formState.name}
            onChange={(event) =>
              setFormState((prev) => ({
                ...prev,
                name: event.target.value,
              }))
            }
            placeholder={t("createTournament.tournamentNamePlaceholder")}
            className="bg-transparent text-base font-semibold uppercase text-white placeholder:text-white/40"
          />
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-1.5">
            <label className="text-xs uppercase tracking-wide text-white/70">
              {t("createTournament.password")}
            </label>
            <Popover>
              <PopoverTrigger asChild>
                <InfoIcon className="w-4 h-4 -mt-1 text-white/70" />
              </PopoverTrigger>
              <PopoverContent className="max-w-md text-xs text-left">
                <p className="text-white text-shadow-black-sm">
                  {t("createTournament.passwordOptional")}
                </p>
                <p className="text-white text-shadow-black-sm mt-1">
                  {t("createTournament.passwordAlphanumericOnly")}
                </p>
                <p className="text-white text-shadow-black-sm mt-1">
                  {t("createTournament.passwordLength", {
                    min: MIN_PASSWORD_LENGTH,
                    max: MAX_PASSWORD_LENGTH,
                  })}
                </p>
              </PopoverContent>
            </Popover>
          </div>
          <div className="relative">
            <Input
              autoComplete="nope"
              type={showPassword ? "text" : "password"}
              value={formState.password}
              onChange={(event) => {
                const value = event.target.value
                  .replace(/[^a-zA-Z0-9]/g, "")
                  .toLowerCase()
                  .slice(0, MAX_PASSWORD_LENGTH);
                setFormState((prev) => ({ ...prev, password: value }));
              }}
              placeholder={t("createTournament.passwordPlaceholder")}
              className="bg-transparent text-base font-semibold uppercase text-white placeholder:text-white/40 pr-10"
            />
            <button
              type="button"
              onClick={() => setShowPassword((prev) => !prev)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-white/70 hover:text-white"
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? (
                <EyeOff className="w-4 h-4" />
              ) : (
                <Eye className="w-4 h-4" />
              )}
            </button>
          </div>
          {isPasswordError && (
            <p className="text-xs text-red-400">
              {t("createTournament.passwordLengthError", {
                min: MIN_PASSWORD_LENGTH,
                max: MAX_PASSWORD_LENGTH,
              })}
            </p>
          )}
        </div>
        <div className="flex gap-3">
          <div className="flex-1 space-y-1">
            <label className="text-xs uppercase tracking-wide text-white/70">
              {t("createTournament.startDate")}
            </label>
            <Input
              type="date"
              min={todayDate}
              max={maxStartDate}
              value={startDateInput}
              onChange={(event) => {
                const nextValue = event.target.value;
                setDateMaxError(nextValue ? nextValue > maxStartDate : false);
                setStartDateInput(nextValue);
                setFormState((prev) => ({
                  ...prev,
                  start:
                    nextValue && startTimeInput
                      ? `${nextValue}T${startTimeInput}`
                      : "",
                }));
              }}
              className={cn(
                "bg-transparent text-base font-semibold uppercase text-white placeholder:text-white/40 appearance-none scheme-dark",
              )}
            />
            <p className="text-xs text-white/60">
              {t("createTournament.max7DaysAhead")}
            </p>
          </div>
          <div className="w-40 flex-none space-y-1">
            <label className="text-xs uppercase tracking-wide text-white/70">
              {t("createTournament.startTime")}
            </label>
            <Input
              type="time"
              value={startTimeInput}
              onChange={(event) => {
                const nextValue = event.target.value;
                setStartTimeInput(nextValue);
                setFormState((prev) => ({
                  ...prev,
                  start:
                    startDateInput && nextValue
                      ? `${startDateInput}T${nextValue}`
                      : "",
                }));
              }}
              className="bg-transparent text-base font-semibold uppercase text-white placeholder:text-white/40 appearance-none scheme-dark"
            />
            <p className="text-xs text-white/60 text-end">
              Your Timezone: {getUtcOffset()}
            </p>
          </div>
        </div>
        <div className="flex gap-3">
          <div className="flex-1 space-y-1">
            <label className="text-xs uppercase tracking-wide text-white/70">
              {t("createTournament.duration")}
            </label>
            <CustomSelect
              value={formState.duration}
              options={durationOptions}
              placeholder={t("createTournament.durationPlaceholder")}
              onChange={(value) =>
                setFormState((prev) => ({ ...prev, duration: value }))
              }
            />
          </div>

          <div className="flex-1 space-y-1">
            <div className="flex items-center gap-1.5">
              <label className="text-xs uppercase tracking-wide text-white/70">
                {t("createTournament.entryFeePerSquad")}
              </label>
              <Popover>
                <PopoverTrigger asChild>
                  <InfoIcon className="w-4 h-4 -mt-1 text-white/70" />
                </PopoverTrigger>
                <PopoverContent className="max-w-md text-xs text-left">
                  <p className="text-white text-shadow-black-sm">
                    {t("createTournament.entryFeeInfo")}
                  </p>
                </PopoverContent>
              </Popover>
            </div>
            <Input
              type="number"
              min={MIN_ENTRY_FEE}
              max={MAX_ENTRY_FEE}
              step="0.001"
              value={formState.entryFee}
              onChange={(event) =>
                setFormState((prev) => {
                  const nextValue = event.target.value;
                  const numericValue = Number(nextValue);
                  setEntryFeeMaxError(
                    Number.isFinite(numericValue) &&
                      numericValue > MAX_ENTRY_FEE,
                  );
                  return {
                    ...prev,
                    entryFee: nextValue,
                  };
                })
              }
              placeholder={t("createTournament.entryFeePlaceholder")}
              className="bg-transparent text-base font-semibold uppercase text-white placeholder:text-white/40"
            />
            {isEntryFeeMaxError ? (
              <p className="text-xs text-red-400 text-end">
                {t("createTournament.maximumEntryFee", {
                  max: MAX_ENTRY_FEE.toFixed(0),
                })}
              </p>
            ) : (
              <p className="text-xs text-white/60 text-end">
                {t("createTournament.minimumEntryFee", {
                  min: MIN_ENTRY_FEE.toFixed(2),
                })}
              </p>
            )}
          </div>
        </div>
        <div className="flex gap-3">
          <div className="flex-1 space-y-1">
            <div className="flex items-center gap-1.5">
              <label className="text-xs uppercase tracking-wide text-white/70">
                {t("createTournament.maxJoinedSquad")}
              </label>
              <Popover>
                <PopoverTrigger asChild>
                  <InfoIcon className="w-4 h-4 -mt-1 text-white/70" />
                </PopoverTrigger>
                <PopoverContent className="max-w-md text-xs text-left">
                  <p className="text-white text-shadow-black-sm">
                    {t("createTournament.maxJoinedSquadInfo")}
                  </p>
                </PopoverContent>
              </Popover>
            </div>
            <Input
              type="number"
              min={2}
              max={MAX_CAPACITY}
              value={formState.capacity}
              onChange={(event) => {
                const newCapacity = event.target.value;
                const capacityNum = Number(newCapacity);
                setCapacityMaxError(
                  Number.isFinite(capacityNum) && capacityNum > MAX_CAPACITY,
                );

                setFormState((prev) => {
                  const updated = { ...prev, capacity: newCapacity };
                  if (Number.isFinite(capacityNum) && capacityNum > 0) {
                    const effectiveCapacity = Math.min(
                      capacityNum,
                      MAX_CAPACITY,
                    );
                    const newMax = Math.floor(effectiveCapacity / 2);
                    const currentMaxMultiEntry = Number(prev.maxMultiEntry);
                    if (
                      Number.isFinite(currentMaxMultiEntry) &&
                      currentMaxMultiEntry > newMax
                    ) {
                      updated.maxMultiEntry = newMax.toString();
                      setMaxMultiEntryError(false);
                    }
                  }
                  return updated;
                });
              }}
              placeholder={t("createTournament.maxSquadPlaceholder")}
              className="bg-transparent text-base font-semibold uppercase text-white placeholder:text-white/40"
            />
            {isCapacityMaxError ? (
              <p className="text-xs text-red-400">
                {t("createTournament.maximumSquads", { max: MAX_CAPACITY })}
              </p>
            ) : (
              <p className="text-xs text-white/60">
                {t("createTournament.minimumSquads")}
              </p>
            )}
          </div>
          <div className="flex-1 space-y-1">
            <div className="flex items-center gap-1.5">
              <label className="text-xs uppercase tracking-wide text-white/70">
                {t("createTournament.maxSquadsPerPlayer")}
              </label>
              <Popover>
                <PopoverTrigger asChild>
                  <InfoIcon className="w-4 h-4 -mt-1 text-white/70" />
                </PopoverTrigger>
                <PopoverContent className="max-w-md text-xs text-left">
                  <p className="text-white text-shadow-black-sm">
                    {t("createTournament.maxSquadsPerPlayerInfo")}
                  </p>
                </PopoverContent>
              </Popover>
            </div>
            <Input
              type="number"
              min={1}
              max={maxMultiEntryMax}
              value={formState.maxMultiEntry}
              disabled={isMaxMultiEntryDisabled}
              onChange={(event) => {
                const value = event.target.value;
                const numValue = Number(value);
                if (!value) {
                  setMaxMultiEntryError(false);
                  setFormState((prev) => ({ ...prev, maxMultiEntry: "" }));
                  return;
                }
                if (
                  Number.isFinite(numValue) &&
                  maxMultiEntryMax !== undefined
                ) {
                  setMaxMultiEntryError(numValue > maxMultiEntryMax);
                } else {
                  setMaxMultiEntryError(false);
                }
                setFormState((prev) => ({ ...prev, maxMultiEntry: value }));
              }}
              placeholder={t("createTournament.maxMultiEntryPlaceholder")}
              className={cn(
                "bg-transparent text-base font-semibold uppercase placeholder:text-white/40",
                isMaxMultiEntryDisabled
                  ? "text-white/40 cursor-not-allowed"
                  : "text-white",
              )}
            />
            {isMaxMultiEntryError && maxMultiEntryMax !== undefined && (
              <p className="text-xs text-red-400">
                {t("createTournament.maximumEntries", {
                  max: maxMultiEntryMax,
                })}
              </p>
            )}
          </div>
        </div>
        {/* <div className="space-y-2">
          <label className="text-xs uppercase tracking-wide text-white/70">
            Payout Weights
          </label>
          <div className="relative">
            <Input
              readOnly
              value={
                payoutWeights
                  ? "Configured"
                  : "Click to configure payout weights"
              }
              onClick={() => {
                const capacityValue = Number(formState.capacity);
                const entryFeeValue = Number(formState.entryFee);

                if (capacityValue <= 0) {
                  toast.error("Error", {
                    description: "Please enter Max Squad first",
                  });
                  return;
                }

                if (entryFeeValue < MIN_ENTRY_FEE) {
                  toast.error("Error", {
                    description: "Please enter Entry Fee first",
                  });
                  return;
                }

                setIsPayoutWeightDialogOpen(true);
              }}
              className={cn(
                "bg-transparent text-base font-semibold uppercase cursor-pointer pr-10",
                payoutWeights
                  ? "text-[#9EF665]"
                  : "text-white placeholder:text-white/40",
              )}
              placeholder="Click to configure payout weights"
            />
            {payoutWeights && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <svg
                  className="w-5 h-5 text-[#9EF665]"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={3}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
            )}
          </div>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleSimulate}
              disabled={!canSimulatePayout}
              className="text-xs font-semibold cursor-pointer uppercase text-white/70 hover:text-white disabled:cursor-not-allowed disabled:text-white/40"
            >
              simulate payout
            </button>
          </div>
        </div> */}
        <div className="space-y-1">
          <label className="text-xs uppercase tracking-wide text-white/70">
            {t("createTournament.payoutPlaces")}
          </label>
          <CustomSelect
            value={formState.payoutPlaces}
            options={payoutOptions}
            placeholder={t("createTournament.selectPayout")}
            disabled={
              !formState.entryFee ||
              Number(formState.entryFee) <= 0 ||
              !formState.capacity ||
              Number(formState.capacity) <= 0
            }
            onChange={(value) =>
              setFormState((prev) => ({ ...prev, payoutPlaces: value }))
            }
          />
          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleSimulate}
              disabled={!canSimulatePayout}
              className="text-xs font-semibold cursor-pointer underline disabled:no-underline uppercase text-white hover:text-white disabled:cursor-not-allowed disabled:text-white/40"
            >
              {t("createTournament.simulatePayout")}
            </button>
          </div>
        </div>
        <div className="space-y-2">
          <label className="text-xs uppercase tracking-wide text-white/70">
            {t("createTournament.yourSquad")}
          </label>
          <SquadMatchTypeSheet
            open={isSquadMatchTypeSheetOpen}
            onOpenChange={setIsSquadMatchTypeSheetOpen}
            matchType={formState.matchType}
            onMatchTypeChange={(value) =>
              setFormState((prev) => ({ ...prev, matchType: value }))
            }
            selectedTokens={selectedTokens}
            onSelectToken={handleSelectToken}
            activeSlotIndex={activeSlotIndex}
            setActiveSlotIndex={setActiveSlotIndex}
          >
            {!playerCardData ? (
              <button
                type="button"
                className="custom-input-1 cursor-pointer rounded-none w-full items-center justify-center whitespace-nowrap text-base font-semibold uppercase text-white transition-all duration-100"
              >
                {t("createTournament.configureSquadAndMatchType")}
              </button>
            ) : (
              <div className="w-full cursor-pointer">
                <PlayerCardCompact
                  player={playerCardData}
                  getPositionLabel={getPositionLabel}
                />
              </div>
            )}
          </SquadMatchTypeSheet>
        </div>
        <div className="mt-auto space-y-3">
          <p className="text-xs text-white/60 text-start px-2">
            {t("createTournament.creatorRewardInfo")}
          </p>
          <DefaultButton
            variant="green"
            disabled={!canSubmit || isPending}
            className="w-full text-base font-black uppercase disabled:opacity-60"
            type="submit"
          >
            {isPending
              ? t("createTournament.creating")
              : t("createTournament.createButton")}
          </DefaultButton>
        </div>
      </form>
      {/* <PayoutWeightDialog
        open={isPayoutWeightDialogOpen}
        onOpenChange={setIsPayoutWeightDialogOpen}
        capacity={Number(formState.capacity) || 50}
        initialWeights={payoutWeights || undefined}
        onApply={(weights) => setPayoutWeights(weights)}
      /> */}
      <TournamentSummaryDialog
        open={isSummaryDialogOpen}
        onOpenChange={setIsSummaryDialogOpen}
        onConfirm={handleCreateTournament}
        isConfirming={isPending}
        tournamentName={formState.name.trim()}
        startDate={formState.start}
        duration={formState.duration}
        entryFee={formState.entryFee}
        capacity={formState.capacity}
        maxMultiEntry={formState.maxMultiEntry}
        payoutPlaces={formState.payoutPlaces}
        matchType={formState.matchType}
        password={formState.password.trim().toLowerCase() || undefined}
        playerCardData={playerCardData}
        getPositionLabel={getPositionLabel}
        hideTrophies
      />
      {formState.payoutPlaces && (
        <PayoutSimulationDialog
          open={isSimulationOpen}
          onOpenChange={(open) => {
            setSimulationOpen(open);
            // Reset state after dialog animation completes
            if (!open) {
              setTimeout(() => {
                setIsSimulationFromButton(false);
              }, 300);
            }
          }}
          entryFee={Number(formState.entryFee)}
          capacity={Number(formState.capacity)}
          payoutPlaces={formState.payoutPlaces}
          onConfirm={handleCreateTournament}
          isConfirming={isPending}
          showConfirmButton={!isSimulationFromButton}
        />
      )}
    </div>
  );
}
